/**
 * One ticket, in full.
 *
 * Reading order is deliberate: what the employee has to *do* comes first (open
 * questions from IT, a proposed fix waiting for a yes or no), then what the
 * ticket *is*, then what has happened to it. SLA sits in the status strip at
 * the top because "when will this be dealt with" is the question the portal
 * exists to answer.
 *
 * Everything writable is gated on `permitted_operations` — the server decides
 * what Ivanti will accept, and the UI never offers an action it will refuse.
 */

import { ArrowLeft, Icon, RotateCcw, SearchX, SquareCheck } from "@norican/icons";
import {
  Alert,
  Button,
  Pagination,
  Separator,
  StatusStrip,
  Switch,
  Typography,
} from "@norican/react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import {
  AppBreadcrumbs,
  CopyLinkMenu,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  LoadingState,
  normaliseApiError,
  PageContainer,
  RelativeTime,
  SectionCard,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

import type { TicketDetail } from "./api/contract";
import { useTicket, useTicketTimeline, useTranslateTo } from "./api/queries";
import { KioskTicketNumberNote } from "./components/kiosk-ticket-number-note";
import { NamedOnNotice } from "./components/named-on";
import { ReportProblemPanel } from "./components/report-problem-panel";
import { SlaSummary } from "./components/sla-indicator";
import { TicketActivity } from "./components/ticket-activity";
import { TicketApprovals } from "./components/ticket-approvals";
import { TicketAttachments } from "./components/ticket-attachments";
import { TicketAttributes } from "./components/ticket-attributes";
import { TicketPriorityBadge, TicketStatusBadge } from "./components/ticket-badges";
import { TicketCloseDialog, TicketReopenDialog } from "./components/ticket-close-dialog";
import { TicketCommentForm } from "./components/ticket-comment-form";
import { TicketErrorBoundary } from "./components/ticket-error-boundary";
import { TicketInfoRequests } from "./components/ticket-info-requests";
import { TicketProgress } from "./components/ticket-progress";
import { TicketResolutionPanel } from "./components/ticket-resolution-panel";
import { TranslatableTextView } from "./components/translatable-text";
import {
  TranslationPreferenceProvider,
  useTranslationPreference,
} from "./components/translation-preference";
import { useClosureRequested } from "./lib/closure-requested";
import { finishedReplyMode } from "./lib/finished-reply";
import { latestItNote } from "./lib/latest-it-note";
import { isNamedView } from "./lib/named-on";
import { assessSla, isPermitted, ticketReferenceKey } from "./lib/ticket-display";
import { isTicketNotFound } from "./lib/ticket-number";
import { anyTranslated } from "./lib/translatable";

/** Journal entries per page. The contract caps `limit` at 100. */
const TIMELINE_PAGE_SIZE = 50;

export function TicketDetailPage() {
  return (
    <TicketErrorBoundary>
      <TranslationPreferenceProvider>
        <TicketDetailView />
      </TranslationPreferenceProvider>
    </TicketErrorBoundary>
  );
}

function TicketDetailView() {
  const { t } = useT("tickets");
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const translateTo = useTranslateTo();
  const ticketQuery = useTicket(ticketId, translateTo);
  // The ticket number, once it is known — that is what a user scanning tabs is
  // looking for. Falls back to the list title while the read is in flight.
  useDocumentTitle(
    ticketQuery.data === undefined
      ? t("title")
      : `${ticketQuery.data.ticket_number} · ${ticketQuery.data.subject.text}`,
  );

  if (ticketQuery.isPending) {
    return (
      <PageContainer title={t("detail.loadingTitle")}>
        <LoadingState />
      </PageContainer>
    );
  }

  // The way out of a dead end is an action, so it is drawn as one: a design
  // system Button, not text that happens to be blue.
  const backToList = (
    <Button
      variant="outline"
      tone="neutral"
      startIcon={<Icon icon={ArrowLeft} size="sm" />}
      onClick={() => void navigate("/tickets")}
    >
      {t("detail.backToList")}
    </Button>
  );

  if (ticketQuery.isError || ticketQuery.data === undefined) {
    const normalised = normaliseApiError(ticketQuery.error);
    // Not theirs or not anyone's: a Retry would only fail the same way, so say
    // it cannot be found and point at My tickets — as the by-number page does.
    if (isTicketNotFound(normalised.status, normalised.code)) {
      return (
        <PageContainer title={t("detail.notFoundTitle")}>
          <EmptyStateBlock
            icon={<Icon icon={SearchX} size="lg" className="text-fg-muted" />}
            title={t("byNumber.notFoundBody")}
            description={null}
            actions={backToList}
          />
        </PageContainer>
      );
    }
    return (
      <PageContainer title={t("detail.errorTitle")}>
        <ErrorState
          error={ticketQuery.error}
          onRetry={() => void ticketQuery.refetch()}
          actions={backToList}
        />
      </PageContainer>
    );
  }

  return <TicketDetailContent ticket={ticketQuery.data} />;
}

interface TicketDetailContentProps {
  ticket: TicketDetail;
}

function TicketDetailContent({ ticket }: TicketDetailContentProps) {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  const translateTo = useTranslateTo();
  const { showOriginal, setShowOriginal } = useTranslationPreference();

  const [closeOpen, setCloseOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  /* An untriaged incident is not closed by Close: IT is asked to, and the
     ticket comes back unchanged. Say so on the page and take the button away
     for this session, so nobody asks twice. */
  const { requested: closureRequested, markRequested: markClosureRequested } = useClosureRequested(
    ticket.ticket_id,
  );
  const canClose = isPermitted(ticket, "CLOSE");
  // Someone named on the request, not its Customer: read and comment only.
  const named = isNamedView(ticket);
  const [timelinePage, setTimelinePage] = useState(1);
  // The timeline reads oldest-first, so on a long ticket IT's latest reply is
  // on the last page — and people arrive here from an Ivanti email about that
  // reply. Jump there once, when the total is first known; after that the
  // page is the reader's to choose. "jumping" covers the fetch of the last
  // page, so page 1 is not shown under the last page's number meanwhile.
  const [latest, setLatest] = useState<"locating" | "jumping" | "done">("locating");

  const timelineQuery = useTicketTimeline(ticket.ticket_id, {
    limit: TIMELINE_PAGE_SIZE,
    offset: (timelinePage - 1) * TIMELINE_PAGE_SIZE,
    order: "ASC",
    translate_to: translateTo,
  });

  /* "Waiting on you" quotes what IT last asked (LIVE SR 10661, 9 Oct): newest
     first, so the latest note is on the first page whatever page Activity is
     showing. Only fetched while the ticket is waiting on this person. */
  const waitingOnMe =
    ticket.status.is_waiting_on_requester && ticket.viewed_as_migrator !== true && !named;
  const latestQuery = useTicketTimeline(
    ticket.ticket_id,
    { limit: 25, offset: 0, order: "DESC", translate_to: translateTo },
    waitingOnMe,
  );
  const itAsked = waitingOnMe ? latestItNote(latestQuery.data?.items ?? []) : null;
  const canReply = isPermitted(ticket, "ADD_COMMENT");
  /* A reply on a finished ticket reopens it / tells IT, or, where nothing can
     (Closed), "Report a problem" takes its place (LIVE SR 10578, 9 Oct). */
  const finishedMode = finishedReplyMode(ticket);
  const jumpToReply = () => {
    const box = document.querySelector<HTMLTextAreaElement>('textarea[name="comment"]');
    box?.scrollIntoView({ behavior: "smooth", block: "center" });
    box?.focus({ preventScroll: true });
  };

  const sla = assessSla(ticket);
  const hasTranslation =
    anyTranslated([ticket.subject, ticket.description, ticket.resolution]) ||
    (timelineQuery.data?.items ?? []).some((entry) => entry.body?.translated === true);

  const timelineTotal = timelineQuery.data?.page.total;
  const timelinePageCount =
    timelineTotal != null
      ? Math.max(1, Math.ceil(timelineTotal / TIMELINE_PAGE_SIZE))
      : timelinePage + (timelineQuery.data?.page.has_more === true ? 1 : 0);

  // Adjusted while rendering rather than in an effect, so page 1 is never
  // painted only to be swapped out a frame later.
  if (latest === "locating" && timelineQuery.data !== undefined) {
    const jump = timelineTotal != null && timelinePageCount > timelinePage;
    setLatest(jump ? "jumping" : "done");
    if (jump) {
      setTimelinePage(timelinePageCount);
    }
  } else if (latest === "jumping" && !timelineQuery.isPlaceholderData) {
    setLatest("done");
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      {/* 23 Oct: share it in Teams or email. IT and admins get the ITSM link
          too; a requester only the HelpDesk link. */}
      <CopyLinkMenu
        kind={ticket.kind}
        number={ticket.ticket_number}
        summary={ticket.subject.text}
      />
      {hasTranslation ? (
        <Switch.Root
          checked={showOriginal}
          onCheckedChange={setShowOriginal}
          label={t("translated.showOriginalSwitch")}
          labelPosition="start"
        />
      ) : null}
      {isPermitted(ticket, "REOPEN") ? (
        <Button
          variant="outline"
          tone="neutral"
          startIcon={<Icon icon={RotateCcw} size="sm" />}
          onClick={() => setReopenOpen(true)}
        >
          {t("operation.REOPEN")}
        </Button>
      ) : null}
      {canClose && !closureRequested ? (
        <Button
          tone="danger"
          variant="outline"
          startIcon={<Icon icon={SquareCheck} size="sm" />}
          onClick={() => setCloseOpen(true)}
        >
          {t("operation.CLOSE")}
        </Button>
      ) : null}
    </div>
  );

  const breadcrumbs = (
    <AppBreadcrumbs
      items={[
        // Back to the tab it was opened from.
        { label: t("title"), to: named ? "/tickets?view=named-on" : "/tickets" },
        { label: t("detail.title", { number: ticket.ticket_number }) },
      ]}
    />
  );

  return (
    <PageContainer
      // Same condensed display face as the ticket list, so moving between the
      // two feels like one product rather than two admin screens.
      title={
        <Typography as="span" variant="display-md" className="text-3xl sm:text-4xl">
          {t(ticketReferenceKey(ticket.kind, "heading"), { number: ticket.ticket_number })}
        </Typography>
      }
      subtitle={<TranslatableTextView value={ticket.subject} />}
      actions={actions}
      breadcrumbs={breadcrumbs}
      width="wide"
    >
      {/* On a shared device the only tickets that open are the ones just
          filed here, so the number to write down goes first (1 Oct). */}
      <KioskTicketNumberNote number={ticket.ticket_number} />

      {ticket.viewed_as_admin ? (
        <Alert tone="info" title={t("detail.viewedAsAdmin")}>
          {t("detail.viewedAsAdminHint")}
        </Alert>
      ) : null}

      {/* Read-only: the server offers no operations on a migrated ticket, so
          every action below is already hidden by `permitted_operations`. */}
      {ticket.viewed_as_migrator === true ? (
        <Alert tone="info" title={t("detail.viewedAsMigrator")}>
          {t("detail.viewedAsMigratorHint", {
            customer: ticket.requester.display_name,
          })}
        </Alert>
      ) : null}

      {/* Named on someone else's request (2 Oct): the server has already left
          only ADD_COMMENT in `permitted_operations`, so this just says why. */}
      <NamedOnNotice ticket={ticket} />

      {/* Only while it is still closable: once IT has closed it, the status
          says so and this would be stale. */}
      {canClose && closureRequested ? (
        <Alert tone="info" title={t("close.requestedTitle")}>
          {t("close.requested")}
        </Alert>
      ) : null}

      {ticket.partial_translation ? <Alert tone="info">{t("translated.partial")}</Alert> : null}

      {/* A request can be filed holding only some of what was typed, and the
          backend says so — the catalog page used to discard that and show an
          unqualified success. The warning lives here rather than on a toast
          because it survives the navigation and is still true tomorrow. */}
      {(ticket.unattached_answers ?? []).length > 0 ? (
        <Alert tone="warning" title={t("detail.unattachedTitle")}>
          {t("detail.unattachedBody", { fields: (ticket.unattached_answers ?? []).join(", ") })}
        </Alert>
      ) : null}

      {/* Worse than a missing answer: the request exists and nothing will ever
          pick it up, because the step that starts it did not run. */}
      {ticket.workflow_trigger_failed === true ? (
        <Alert tone="warning" title={t("detail.workflowStalledTitle")}>
          {t("detail.workflowStalledBody")}
        </Alert>
      ) : null}

      {/* Waiting for Customer means nothing moves until this person acts, so it
          is the one state that must not read as just another status word. */}
      {/* Waiting on the Customer, not on someone merely named on it. */}
      {waitingOnMe ? (
        <Alert tone="warning" title={t("detail.waitingOnYouTitle")}>
          <div className="flex flex-col gap-2">
            {itAsked?.body == null ? (
              <span>{t("detail.waitingOnYouHint")}</span>
            ) : (
              <>
                <Typography as="div" variant="body-sm" className="font-semibold">
                  {t("detail.itAsked", { name: itAsked.actor.display_name })}
                </Typography>
                <blockquote className="border-l-2 border-current pl-3">
                  <TranslatableTextView value={itAsked.body} multiline />
                </blockquote>
                <span>{t("detail.itAskedHint")}</span>
              </>
            )}
            {canReply ? (
              <Button size="sm" variant="outline" className="self-start" onClick={jumpToReply}>
                {t("detail.replyNow")}
              </Button>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {/* Fulfilled is completed, not finished. Norican parks a request here for
          three days and a reply sends it back to Active — so the window is
          explained in words rather than as a countdown, because the portal
          cannot read the deadline from Ivanti and will not invent one. */}
      {ticket.status.code === "FULFILLED" ? (
        <Alert tone="info" title={t("detail.fulfilledTitle")}>
          {t("detail.fulfilledHint")}
        </Alert>
      ) : null}

      {/* An administrator can add a status at any time. Saying so is better
          than showing a word with no explanation, and far better than the old
          behaviour, which quietly filed the unfamiliar under "closed". */}
      {ticket.status.is_recognised === false ? (
        <Alert tone="info" title={ticket.status.provider_status}>
          {t("detail.unknownStatusHint")}
        </Alert>
      ) : null}

      {/* The four-stop track sits above the numbers, because "where is this
          up to?" is the question someone opens a ticket to answer, and a
          status word alone does not answer it for a non-technical reader. */}
      <TicketProgress status={ticket.status.code} />

      <StatusStrip>
        <StatusStrip.Cell label={t("field.status")}>
          <TicketStatusBadge status={ticket.status} kind={ticket.kind} />
        </StatusStrip.Cell>
        <StatusStrip.Cell label={t("field.priority")}>
          <TicketPriorityBadge code={ticket.priority_code} />
        </StatusStrip.Cell>
        <StatusStrip.Cell label={t("sla.label")} status={sla.tone}>
          <SlaSummary ticket={ticket} />
        </StatusStrip.Cell>
        <StatusStrip.Cell label={t("field.updated")}>
          <RelativeTime value={ticket.updated_at} />
        </StatusStrip.Cell>
      </StatusStrip>

      <TicketInfoRequests
        ticketId={ticket.ticket_id}
        requests={ticket.open_info_requests ?? []}
        canAnswer={isPermitted(ticket, "ANSWER_INFO_REQUEST")}
      />

      {ticket.pending_confirmation != null && isPermitted(ticket, "CONFIRM_RESOLUTION") ? (
        <TicketResolutionPanel
          ticketId={ticket.ticket_id}
          confirmation={ticket.pending_confirmation}
        />
      ) : null}

      <SectionCard title={t("detail.overview")}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Typography variant="label" tone="muted">
              {t("field.description")}
            </Typography>
            {/* `as="div"`: an Ivanti description is HTML, and its paragraphs
                cannot be nested inside Typography's default `<p>`. */}
            <Typography as="div" variant="body">
              <TranslatableTextView value={ticket.description} multiline />
            </Typography>
          </div>
          <Separator />
          <TicketAttributes ticket={ticket} />
        </div>
      </SectionCard>

      {(ticket.approvals ?? []).length > 0 ? (
        <SectionCard title={t("detail.approvals")}>
          <TicketApprovals approvals={ticket.approvals ?? []} />
        </SectionCard>
      ) : null}

      <SectionCard title={t("detail.attachments")}>
        <TicketAttachments ticketId={ticket.ticket_id} attachments={ticket.attachments ?? []} />
      </SectionCard>

      <SectionCard title={t("detail.activity")}>
        <div className="flex flex-col gap-6">
          {timelineQuery.isPending || latest === "jumping" ? (
            <LoadingRows rows={4} />
          ) : timelineQuery.isError ? (
            <ErrorState
              variant="inline"
              error={timelineQuery.error}
              onRetry={() => void timelineQuery.refetch()}
            />
          ) : (
            <>
              <TicketActivity entries={timelineQuery.data?.items ?? []} />
              {timelinePageCount > 1 ? (
                <Pagination
                  page={timelinePage}
                  pageCount={timelinePageCount}
                  onPageChange={setTimelinePage}
                  label={tCommon("pagination.label")}
                  prevLabel={tCommon("pagination.previous")}
                  nextLabel={tCommon("pagination.next")}
                  hideSummary
                  formatStatus={({ page: current, pageCount: count }) =>
                    tCommon("pagination.status", { page: current, pageCount: count })
                  }
                />
              ) : null}
            </>
          )}

          {canReply ? (
            <>
              <Separator />
              <TicketCommentForm
                ticketId={ticket.ticket_id}
                allowAttachments={!named}
                finished={
                  finishedMode === "reopen" || finishedMode === "notify" ? finishedMode : undefined
                }
              />
            </>
          ) : finishedMode === "report" ? (
            <>
              <Separator />
              <ReportProblemPanel ticketId={ticket.ticket_id} kind={ticket.kind} />
            </>
          ) : null}
        </div>
      </SectionCard>

      <TicketCloseDialog
        ticketId={ticket.ticket_id}
        open={closeOpen}
        onOpenChange={setCloseOpen}
        onClosureRequested={markClosureRequested}
      />
      <TicketReopenDialog
        ticketId={ticket.ticket_id}
        open={reopenOpen}
        onOpenChange={setReopenOpen}
      />
    </PageContainer>
  );
}
